import React from 'react';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Mail, User, MessageSquare, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import Section from './ui/section';

export default function Contact(){
    const [status, setStatus] = useState('idle');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');

        try {
            const formData = new FormData(e.currentTarget);
            const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxU3ri-rXETek8HCqrtBAfGlj5dgQkGorn-INh7MOA9jpSIV4WZ4c7wwkr347pqmIM/exec';

            const response  = await fetch(SCRIPT_URL,{
                method:'POST',
                body: formData,
            })

            const result = await response.json();
            
            if (result.result==='success'){
                setStatus('success');
                if (e.currentTarget) {
                    e.currentTarget.reset();
                }

                setTimeout(() => setStatus('idle'), 3000);
            }
            else{
                setStatus('error');
            }
        }
        catch (error) {
            setStatus('error');
            console.error(error);

            setTimeout(() => setStatus('idle'), 3000);
        } 
    }

    return(
        <section className='relative flex flex-col items-center text-center text-white max-w-7xl mx-auto px-6 py-24 gap-10 overflow-hidden' id='contact'>
            {/* Background effects */}
            <div className="absolute inset-0 w-full bg-gradient-to-b from-black via-purple-950/60 to-black -z-10" />
            <div className="absolute top-0 right-1/4 bg-gradient-radial from-cyan-500/20 via-transparent to-transparent blur-3xl -z-10 w-96 h-96" />
            <div className="absolute bottom-0 left-1/4 bg-gradient-radial from-fuchsia-500/20 via-transparent to-transparent blur-3xl -z-10 w-96 h-96" />

            {/* Section label */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Section label="Contact Section" />
            </motion.div>

            {/* Heading */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center mb-8"
            >
                <h2 className='text-5xl md:text-6xl font-extrabold mb-4 tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-purple-600 drop-shadow-[0_0_20px_rgba(255,0,255,0.6)]'>
                    Get in <span className='text-fuchsia-400'>Touch</span>
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto text-lg">
                    Have questions or feedback? We'd love to hear from you. Send us a message and we'll get back to you as soon as possible.
                </p>
            </motion.div>
            
            <div className='grid md:grid-cols-2 gap-12 items-center w-full max-w-6xl'>
                {/* Left Side - Form */}
                <motion.form
                    onSubmit={handleSubmit}
                    initial={{opacity:0, x:-50}}
                    whileInView={{opacity:1, x:0}}
                    transition={{duration:0.6}}
                    className='space-y-6 bg-gradient-to-br from-black/60 via-purple-900/30 to-black/60 backdrop-blur-md border-2 border-fuchsia-500/30 rounded-2xl p-8 shadow-[0_0_40px_rgba(255,0,255,0.2)]'
                >
                    {/* Name field */}
                    <div className="relative">
                        <label className="block text-gray-300 mb-2 text-sm font-semibold flex items-center gap-2">
                            <User size={16} className="text-fuchsia-400" />
                            Name
                        </label>
                        <input 
                            type="text"
                            name='name'
                            required
                            placeholder='John Smith'
                            className='w-full bg-black/40 border border-fuchsia-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/50 transition-all'
                        />
                    </div>

                    {/* Email field */}
                    <div className="relative">
                        <label className="block text-gray-300 mb-2 text-sm font-semibold flex items-center gap-2">
                            <Mail size={16} className="text-fuchsia-400" />
                            Email
                        </label>
                        <input 
                            type="email"
                            name='email'
                            required
                            placeholder='something@email.com'
                            className='w-full bg-black/40 border border-fuchsia-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/50 transition-all'
                        />
                    </div>

                    {/* Message field */}
                    <div className="relative">
                        <label className="block text-gray-300 mb-2 text-sm font-semibold flex items-center gap-2">
                            <MessageSquare size={16} className="text-fuchsia-400" />
                            Message
                        </label>
                        <textarea
                            name='message'
                            required
                            placeholder='Your message here...'
                            className='w-full bg-black/40 border border-fuchsia-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/50 transition-all h-32 resize-none'
                        />
                    </div>

                    {/* Submit button */}
                    <motion.button
                        type='submit'
                        disabled={status==='loading' || status==='success' || status==='error'}
                        whileHover={status === 'idle' ? { scale: 1.02 } : {}}
                        whileTap={status === 'idle' ? { scale: 0.98 } : {}}
                        animate={{
                            scale: status === 'success' ? 1.05 : 1,
                        }}
                        transition={{ duration: 0.3 }}
                        className={`w-full px-6 py-4 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-all ${
                            status === 'idle' 
                                ? 'bg-gradient-to-r from-fuchsia-500 to-purple-600 shadow-[0_0_20px_rgba(255,0,255,0.5)] hover:shadow-[0_0_30px_rgba(255,0,255,0.7)]'
                                : status === 'success'
                                ? 'bg-green-600 shadow-[0_0_20px_rgba(34,197,94,0.5)]'
                                : status === 'error'
                                ? 'bg-red-600 shadow-[0_0_20px_rgba(220,38,38,0.5)]'
                                : 'bg-blue-600'
                        } disabled:opacity-70 disabled:cursor-not-allowed`}
                    >
                        <AnimatePresence mode='wait'>
                            <motion.span
                                key={status}
                                initial={{opacity:0, y:-10}}
                                animate={{opacity:1, y:0}}
                                exit={{opacity:0, y:10}}
                                transition={{duration:0.2}}
                                className="flex items-center gap-2"
                            >
                                {status === 'idle' && (
                                    <>
                                        <Send size={18} />
                                        Send Message
                                    </>
                                )}
                                {status === 'loading' && (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Sending...
                                    </>
                                )}
                                {status === 'success' && (
                                    <>
                                        <CheckCircle2 size={18} />
                                        Message Sent!
                                    </>
                                )}
                                {status === 'error' && (
                                    <>
                                        <AlertCircle size={18} />
                                        Error! Try Again
                                    </>
                                )}
                            </motion.span>
                        </AnimatePresence>
                    </motion.button>
                </motion.form>

                {/* Right Side - Image */}
                <motion.div
                    initial={{opacity:0, x:50}}
                    whileInView={{opacity:1, x:0}}
                    transition={{duration:0.6, delay:0.2}}
                    className='relative'
                >
                    <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 via-fuchsia-500/20 to-purple-500/20 rounded-2xl blur-2xl" />
                    <motion.img 
                        src="/contact.jpg" 
                        alt="Contact Us" 
                        className='relative w-full h-auto rounded-2xl shadow-2xl border-2 border-fuchsia-500/30'
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                    />
                </motion.div>
            </div>
        </section>
    )
}
